import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Trash2, Users, X, Pencil, Phone, Mail, MapPin, FileSignature } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", phone: "", email: "", address: "", rfc: "", default_credit_days: 0, notes: "", delivery_address: "", signer_name: "", signer_address: "", signer_capacity: "", payment_place: "" };

export default function Clientes() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [toDelete, setToDelete] = useState(null);

  const load = () => api.get("/customers").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...c }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Captura el nombre del cliente");
    try {
      const payload = { ...form, default_credit_days: parseInt(form.default_credit_days) || 0 };
      if (editing) {
        await api.put(`/customers/${editing.id}`, payload);
        toast.success("Cliente actualizado");
      } else {
        await api.post("/customers", payload);
        toast.success("Cliente registrado");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
  };

  const del = async () => {
    try {
      await api.delete(`/customers/${toDelete.id}`);
      toast.success("Cliente eliminado");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <div className="max-w-screen-2xl mx-auto p-6 lg:p-8 space-y-6" data-testid="clientes-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-outfit font-bold text-3xl tracking-tight text-stone-900 dark:text-stone-100">Clientes</h1>
          <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">Catálogo de clientes para registrar ventas y créditos.</p>
        </div>
        {!open && (
          <Button onClick={openNew} data-testid="new-client-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">
            <Plus className="w-4 h-4 mr-1.5" /> Nuevo cliente
          </Button>
        )}
      </div>

      {open && (
        <Card className="p-6 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-outfit font-semibold text-lg text-stone-900 dark:text-stone-100">{editing ? "Editar cliente" : "Nuevo cliente"}</h2>
            <button onClick={() => setOpen(false)} className="text-stone-400 hover:text-stone-700 dark:hover:text-stone-200"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Nombre / Razón social *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name" placeholder="Ej. Quesos del Norte SA" /></Field>
              <Field label="RFC"><Input value={form.rfc} onChange={(e) => setForm({ ...form, rfc: e.target.value.toUpperCase() })} placeholder="Opcional" data-testid="client-rfc" /></Field>
              <Field label="Teléfono"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="10 dígitos" data-testid="client-phone" /></Field>
              <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
              <Field label="Dirección" className="md:col-span-2"><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
              <Field label="Días de crédito por defecto">
                <Input type="number" min="0" value={form.default_credit_days} onChange={(e) => setForm({ ...form, default_credit_days: e.target.value })} placeholder="0 = contado" />
              </Field>
              <Field label="Notas" className="md:col-span-1"><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} /></Field>
            </div>

            <div className="p-4 rounded-md bg-stone-50 dark:bg-stone-900/50 border border-stone-200 dark:border-stone-800">
              <div className="flex items-center gap-1.5 mb-3">
                <FileSignature className="w-3.5 h-3.5 text-[#1E3A8A] dark:text-blue-400" />
                <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Datos para "Entrega de producto y pagaré" (opcional)</Label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Domicilio de entrega (si es distinto al fiscal)" className="md:col-span-2">
                  <Input value={form.delivery_address} onChange={(e) => setForm({ ...form, delivery_address: e.target.value })} placeholder="Déjalo en blanco para usar el domicilio fiscal" data-testid="client-delivery-address" />
                </Field>
                <Field label="Nombre del suscriptor / representante (si es distinto al nombre comercial)">
                  <Input value={form.signer_name} onChange={(e) => setForm({ ...form, signer_name: e.target.value })} data-testid="client-signer-name" />
                </Field>
                <Field label="Carácter con el que firma">
                  <Input value={form.signer_capacity} onChange={(e) => setForm({ ...form, signer_capacity: e.target.value })} placeholder="Ej. Representante Legal" data-testid="client-signer-capacity" />
                </Field>
                <Field label="Domicilio del suscriptor (si es distinto al fiscal)">
                  <Input value={form.signer_address} onChange={(e) => setForm({ ...form, signer_address: e.target.value })} data-testid="client-signer-address" />
                </Field>
                <Field label="Lugar de pago habitual del pagaré">
                  <Input value={form.payment_place} onChange={(e) => setForm({ ...form, payment_place: e.target.value })} placeholder="Ej. Torreón, Coahuila" data-testid="client-payment-place" />
                </Field>
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2 border-t border-stone-200 dark:border-stone-800">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="client-submit-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">{editing ? "Guardar cambios" : "Registrar cliente"}</Button>
            </div>
          </form>
        </Card>
      )}

      <Card className="border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-stone-200 dark:border-stone-800 flex items-center gap-2">
          <Users className="w-4 h-4 text-stone-500" />
          <h3 className="font-outfit font-semibold text-stone-900 dark:text-stone-100">Catálogo de clientes</h3>
          <span className="text-xs text-stone-500 ml-auto">{items.length} registrados</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-100 dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
              <tr>{["Nombre", "RFC", "Contacto", "Dirección", "Crédito", "Acciones"].map((h) => <th key={h} className="px-3 py-2.5 text-left text-[10px] font-outfit font-semibold uppercase tracking-wider text-stone-600 dark:text-stone-400">{h}</th>)}</tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-stone-500">Sin clientes registrados. Crea el primero para poder facturar ventas.</td></tr>
              ) : items.map((c) => (
                <tr key={c.id} className="border-b border-stone-100 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-stone-900/50" data-testid={`client-row-${c.id}`}>
                  <td className="px-3 py-3 text-sm font-medium text-stone-900 dark:text-stone-100">{c.name}</td>
                  <td className="px-3 py-3 text-sm text-stone-700 dark:text-stone-300 font-mono text-xs">{c.rfc || "—"}</td>
                  <td className="px-3 py-3 text-sm text-stone-700 dark:text-stone-300">
                    {c.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3 text-stone-400" /> {c.phone}</div>}
                    {c.email && <div className="flex items-center gap-1 text-xs text-stone-500"><Mail className="w-3 h-3" /> {c.email}</div>}
                    {!c.phone && !c.email && "—"}
                  </td>
                  <td className="px-3 py-3 text-sm text-stone-700 dark:text-stone-300 max-w-[240px] truncate">{c.address ? <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-stone-400 flex-shrink-0" /> {c.address}</span> : "—"}</td>
                  <td className="px-3 py-3 text-sm tabular-nums">{c.default_credit_days > 0 ? <span className="inline-block px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 text-xs font-medium">{c.default_credit_days} días</span> : <span className="text-stone-500">Contado</span>}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEdit(c)} className="p-1.5 rounded-md text-stone-500 hover:text-[#1E3A8A] hover:bg-blue-50 dark:hover:bg-blue-950/40"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => setToDelete(c)} className="p-1.5 rounded-md text-stone-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>¿Eliminar a {toDelete?.name}?</AlertDialogTitle><AlertDialogDescription>Si el cliente tiene ventas asociadas no podrá eliminarse.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700 text-white">Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
